/* CYR Final Week state engine. DOM integration lives in a separate controller. */
(function (root, factory) {
  var engine = factory();
  if (typeof module === "object" && module.exports) module.exports = engine;
  if (root) root.CYRFinalWeekEngine = engine;
})(typeof globalThis !== "undefined" ? globalThis : this, function () {
  "use strict";

  var STATES = Object.freeze({
    PRELAUNCH: "prelaunch",
    ACTIVE: "active",
    GRACE: "grace",
    EXPIRED: "expired",
    CAMPAIGN_ENDED: "campaign_ended"
  });
  var LANES = ["new_primary", "new_follow_up", "old_primary", "old_follow_up"];
  var INTERNAL_QUERY_PARAMETERS = ["isGiveawayLive"];

  function invariant(condition, message) {
    if (!condition) throw new Error(message);
  }

  function parseDate(value) {
    var match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value || "");
    invariant(match, "Invalid calendar date: " + value);
    var result = { year: Number(match[1]), month: Number(match[2]), day: Number(match[3]) };
    var probe = new Date(Date.UTC(result.year, result.month - 1, result.day));
    invariant(
      probe.getUTCFullYear() === result.year && probe.getUTCMonth() === result.month - 1 && probe.getUTCDate() === result.day,
      "Invalid calendar date: " + value
    );
    return result;
  }

  function parseTime(value) {
    var match = /^(\d{2}):(\d{2})$/.exec(value || "");
    invariant(match, "Invalid clock time: " + value);
    var result = { hour: Number(match[1]), minute: Number(match[2]) };
    invariant(result.hour <= 23 && result.minute <= 59, "Invalid clock time: " + value);
    return result;
  }

  function addCalendarDays(dateValue, days) {
    var parts = parseDate(dateValue);
    var result = new Date(Date.UTC(parts.year, parts.month - 1, parts.day + days));
    return [
      result.getUTCFullYear(),
      String(result.getUTCMonth() + 1).padStart(2, "0"),
      String(result.getUTCDate()).padStart(2, "0")
    ].join("-");
  }

  function calendarDayNumber(dateValue) {
    var parts = parseDate(dateValue);
    return Date.UTC(parts.year, parts.month - 1, parts.day) / 86400000;
  }

  function resolveCampaignManifest(manifest, campaignDates) {
    invariant(manifest && typeof manifest === "object", "Final Week manifest is missing");
    invariant(manifest.dateSource === "shopify_metafields", "Unsupported campaign date source");
    invariant(campaignDates && typeof campaignDates === "object", "Shopify campaign dates are missing");

    var giveawayStartDate = campaignDates.giveawayStartDate;
    var giveawayEndDate = campaignDates.giveawayEndDate;
    var startDay = calendarDayNumber(giveawayStartDate);
    var endDay = calendarDayNumber(giveawayEndDate);
    invariant(startDay <= endDay, "Shopify giveaway start date is after its end date");
    invariant(Number.isInteger(manifest.campaignCloseOffsetDays) && manifest.campaignCloseOffsetDays >= 0,
      "Manifest campaign close offset is invalid");

    var resolvedOffers = manifest.offers.map(function (offer) {
      invariant(Number.isInteger(offer.daysBeforeGiveawayEnd) && offer.daysBeforeGiveawayEnd >= 0,
        "Invalid relative schedule on " + offer.path);
      var scheduledDate = addCalendarDays(giveawayEndDate, -offer.daysBeforeGiveawayEnd);
      var scheduledDay = calendarDayNumber(scheduledDate);
      invariant(scheduledDay >= startDay && scheduledDay <= endDay,
        "Resolved offer falls outside the Shopify giveaway window: " + offer.path);
      return Object.assign({}, offer, { scheduledDate: scheduledDate });
    });

    return Object.assign({}, manifest, {
      campaignStartLocalDate: giveawayStartDate,
      giveawayEndLocalDate: giveawayEndDate,
      campaignEndLocalDate: addCalendarDays(giveawayEndDate, manifest.campaignCloseOffsetDays),
      resolvedDateSource: "shopify_metafields",
      offers: resolvedOffers
    });
  }

  function zonedParts(epochMilliseconds, timeZone) {
    var formatter;
    try {
      formatter = new Intl.DateTimeFormat("en-CA", {
        timeZone: timeZone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hourCycle: "h23"
      });
    } catch (error) {
      throw new Error("Unsupported visitor time zone: " + timeZone);
    }

    var values = {};
    formatter.formatToParts(new Date(epochMilliseconds)).forEach(function (part) {
      if (part.type !== "literal") values[part.type] = Number(part.value);
    });
    return values;
  }

  function zonedDateTimeToEpoch(dateValue, timeValue, timeZone) {
    var date = parseDate(dateValue);
    var time = parseTime(timeValue);
    var desiredAsUtc = Date.UTC(date.year, date.month - 1, date.day, time.hour, time.minute, 0, 0);
    var guess = desiredAsUtc;

    for (var attempt = 0; attempt < 4; attempt += 1) {
      var actual = zonedParts(guess, timeZone);
      var actualAsUtc = Date.UTC(
        actual.year,
        actual.month - 1,
        actual.day,
        actual.hour,
        actual.minute,
        actual.second || 0,
        0
      );
      var difference = actualAsUtc - desiredAsUtc;
      if (difference === 0) return guess;
      guess -= difference;
    }

    var roundTrip = zonedParts(guess, timeZone);
    invariant(
      roundTrip.year === date.year && roundTrip.month === date.month && roundTrip.day === date.day
        && roundTrip.hour === time.hour && roundTrip.minute === time.minute,
      "Local time does not exist in " + timeZone + ": " + dateValue + " " + timeValue
    );
    return guess;
  }

  function getOfferWindow(offer, manifest, visitorTimeZone) {
    var nextDate = addCalendarDays(offer.scheduledDate, 1);
    // Every LP assigned to the visitor's current calendar day opens at 00:01.
    // The manifest's RLT send time remains metadata and no longer gates access.
    var accessStartLocalTime = "00:01";
    var startsAt = zonedDateTimeToEpoch(offer.scheduledDate, accessStartLocalTime, visitorTimeZone);
    var graceStartsAt = zonedDateTimeToEpoch(nextDate, "00:00", visitorTimeZone);
    var localGraceEndsAt = zonedDateTimeToEpoch(nextDate, manifest.graceEndLocalTime, visitorTimeZone);
    var campaignEndsAt;

    if (manifest.campaignEndLocalDate && manifest.campaignEndLocalTime) {
      campaignEndsAt = zonedDateTimeToEpoch(
        manifest.campaignEndLocalDate,
        manifest.campaignEndLocalTime,
        visitorTimeZone
      );
    } else {
      campaignEndsAt = Date.parse(manifest.hardStopUtc);
      invariant(Number.isFinite(campaignEndsAt), "Invalid campaign hard stop: " + manifest.hardStopUtc);
    }

    return {
      startsAt: startsAt,
      graceStartsAt: graceStartsAt,
      localGraceEndsAt: localGraceEndsAt,
      campaignEndsAt: campaignEndsAt,
      // Retain the old property as an alias for compatibility with existing analytics/tests.
      hardStopAt: campaignEndsAt,
      expiresAt: Math.min(localGraceEndsAt, campaignEndsAt)
    };
  }

  function evaluateOffer(offer, manifest, now, visitorTimeZone) {
    var nowMilliseconds = now instanceof Date ? now.getTime() : Number(now);
    invariant(Number.isFinite(nowMilliseconds), "Invalid current time");
    var window = getOfferWindow(offer, manifest, visitorTimeZone);
    var state;

    if (nowMilliseconds >= window.campaignEndsAt) state = STATES.CAMPAIGN_ENDED;
    else if (nowMilliseconds < window.startsAt) state = STATES.PRELAUNCH;
    else if (nowMilliseconds < window.graceStartsAt) state = STATES.ACTIVE;
    else if (nowMilliseconds < window.expiresAt) state = STATES.GRACE;
    else state = STATES.EXPIRED;

    return {
      state: state,
      purchasable: state === STATES.ACTIVE || state === STATES.GRACE,
      startsAt: window.startsAt,
      graceStartsAt: window.graceStartsAt,
      localGraceEndsAt: window.localGraceEndsAt,
      campaignEndsAt: window.campaignEndsAt,
      hardStopAt: window.hardStopAt,
      expiresAt: window.expiresAt,
      remainingMilliseconds: Math.max(0, window.expiresAt - nowMilliseconds)
    };
  }

  function validateManifest(manifest) {
    invariant(manifest && typeof manifest === "object", "Final Week manifest is missing");
    invariant(manifest.schemaVersion === 1, "Unsupported Final Week manifest schema");
    invariant(typeof manifest.enabled === "boolean", "Manifest enabled flag must be boolean");
    invariant(typeof manifest.bannerEnabled === "boolean", "Manifest banner flag must be boolean");
    invariant(manifest.dateSource === "shopify_metafields", "Manifest date source is invalid");
    invariant(Number.isInteger(manifest.campaignCloseOffsetDays) && manifest.campaignCloseOffsetDays >= 0,
      "Manifest campaign close offset is invalid");
    var hasLocalCampaignEnd = Boolean(manifest.campaignEndLocalDate || manifest.campaignEndLocalTime);
    if (hasLocalCampaignEnd) {
      invariant(
        typeof manifest.campaignEndLocalDate === "string" && typeof manifest.campaignEndLocalTime === "string",
        "Manifest local campaign end requires both a date and time"
      );
      parseDate(manifest.campaignEndLocalDate);
      parseTime(manifest.campaignEndLocalTime);
    } else {
      invariant(Number.isFinite(Date.parse(manifest.hardStopUtc)), "Manifest hard stop is invalid");
    }
    parseTime(manifest.graceEndLocalTime);
    invariant(/^\/[^?]*$/.test(manifest.fallbackPath), "Manifest fallback path is invalid");
    invariant(
      manifest.targetPolicy === "daily_first_same_lane_then_family_primary",
      "Manifest target policy is invalid"
    );
    invariant(Array.isArray(manifest.allowedQueryParameters), "Manifest query allowlist is missing");
    invariant(
      manifest.allowedQueryParameters.every(function (pattern) {
        if (typeof pattern !== "string" || !/^[a-z0-9_*-]+$/i.test(pattern)) return false;
        return pattern.indexOf("*") === -1
          || (pattern.endsWith("*") && pattern.indexOf("*") === pattern.length - 1);
      }),
      "Manifest query allowlist contains an invalid pattern"
    );
    invariant(Array.isArray(manifest.offers) && manifest.offers.length > 0, "Manifest offers are missing");

    var paths = new Set();
    manifest.offers.forEach(function (offer) {
      invariant(offer && typeof offer === "object", "Manifest contains an invalid offer");
      invariant(/^\/pages\/[a-z0-9-]+$/i.test(offer.path), "Invalid offer path: " + offer.path);
      invariant(!paths.has(offer.path), "Duplicate offer path: " + offer.path);
      paths.add(offer.path);
      parseDate(offer.scheduledDate);
      invariant(Number.isInteger(offer.daysBeforeGiveawayEnd) && offer.daysBeforeGiveawayEnd >= 0,
        "Invalid relative schedule on " + offer.path);
      parseTime(offer.sendLocalTime);
      invariant(offer.sendTimeBasis === "recipient_local", "Unsupported send time basis on " + offer.path);
      invariant(LANES.includes(offer.lane), "Invalid lane on " + offer.path + ": " + offer.lane);
      invariant(Number.isFinite(offer.entries) && offer.entries > 0, "Invalid entries on " + offer.path);
      invariant(Number.isFinite(offer.price) && offer.price > 0, "Invalid price on " + offer.path);
    });

    return true;
  }

  function findOffer(manifest, route) {
    return manifest.offers.find(function (offer) { return offer.path === route; }) || null;
  }

  function isDailyFirstOffer(offer) {
    return /^\/pages\/(?:ol-)?[1-7]d-1(?:-fu)?$/i.test(offer.path);
  }

  function eligibleTargets(manifest, sourceOffer, now, visitorTimeZone, lane) {
    return manifest.offers
      .filter(function (candidate) {
        if (
          candidate.path === sourceOffer.path
          || candidate.lane !== lane
          || !isDailyFirstOffer(candidate)
        ) return false;
        var evaluation = evaluateOffer(candidate, manifest, now, visitorTimeZone);
        return evaluation.state === STATES.ACTIVE || evaluation.state === STATES.GRACE;
      })
      .sort(function (left, right) {
        return getOfferWindow(right, manifest, visitorTimeZone).startsAt
          - getOfferWindow(left, manifest, visitorTimeZone).startsAt;
      });
  }

  function resolveTarget(manifest, sourceOffer, now, visitorTimeZone) {
    var sameLane = eligibleTargets(manifest, sourceOffer, now, visitorTimeZone, sourceOffer.lane);
    if (sameLane.length) return { type: "offer", reason: "same_lane", offer: sameLane[0], path: sameLane[0].path };

    if (
      manifest.targetPolicy === "daily_first_same_lane_then_family_primary"
      && sourceOffer.lane.endsWith("_follow_up")
    ) {
      var familyPrimaryLane = sourceOffer.lane.replace("_follow_up", "_primary");
      var familyPrimary = eligibleTargets(manifest, sourceOffer, now, visitorTimeZone, familyPrimaryLane);
      if (familyPrimary.length) {
        return { type: "offer", reason: "family_primary", offer: familyPrimary[0], path: familyPrimary[0].path };
      }
    }

    return { type: "fallback", reason: "no_eligible_offer", offer: null, path: manifest.fallbackPath };
  }

  function queryParameterAllowed(name, allowlist) {
    if (INTERNAL_QUERY_PARAMETERS.includes(name)) return false;
    return allowlist.some(function (pattern) {
      return pattern.endsWith("*") ? name.startsWith(pattern.slice(0, -1)) : name === pattern;
    });
  }

  function sanitizeQueryString(search, allowlist) {
    var source = new URLSearchParams((search || "").replace(/^\?/, ""));
    var result = new URLSearchParams();
    source.forEach(function (value, name) {
      if (queryParameterAllowed(name, allowlist)) result.append(name, value);
    });
    var serialized = result.toString();
    return serialized ? "?" + serialized : "";
  }

  function buildTargetUrl(targetPath, currentSearch, manifest) {
    invariant(/^\/[^?]*$/.test(targetPath), "Target path is invalid: " + targetPath);
    return targetPath + sanitizeQueryString(currentSearch, manifest.allowedQueryParameters);
  }

  return Object.freeze({
    STATES: STATES,
    validateManifest: validateManifest,
    findOffer: findOffer,
    getOfferWindow: getOfferWindow,
    evaluateOffer: evaluateOffer,
    resolveTarget: resolveTarget,
    resolveCampaignManifest: resolveCampaignManifest,
    sanitizeQueryString: sanitizeQueryString,
    buildTargetUrl: buildTargetUrl,
    zonedDateTimeToEpoch: zonedDateTimeToEpoch
  });
});
